import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import client from '../../api/client';

const STATUS_ORDER = ['open', 'spare_dispatched', 'spare_delivered', 'faulty_picked_up', 'oem_rma_raised', 'closed'];
const STATUS_LABEL = {
  open: 'Open',
  spare_dispatched: 'Spare Dispatched',
  spare_delivered: 'Spare Delivered',
  faulty_picked_up: 'Faulty Picked Up',
  oem_rma_raised: 'OEM RMA Raised',
  closed: 'Closed',
};
const STATUS_COLOR = {
  open: 'bg-blue-100 text-blue-700',
  spare_dispatched: 'bg-amber-100 text-amber-700',
  spare_delivered: 'bg-cyan-100 text-cyan-700',
  faulty_picked_up: 'bg-orange-100 text-orange-700',
  oem_rma_raised: 'bg-purple-100 text-purple-700',
  closed: 'bg-emerald-100 text-emerald-700',
};

const inp = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300';
const lbl = (t) => <p className="text-xs font-semibold text-brand-400 uppercase mb-1">{t}</p>;

function Field({ title, value }) {
  if (!value) return null;
  return (
    <div>
      {lbl(title)}
      <p className="text-sm font-medium text-gray-800">{value}</p>
    </div>
  );
}

function ChallanLink({ url, title }) {
  if (!url) return null;
  return (
    <div>
      {lbl(title)}
      <a href={url} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-brand-500 hover:underline font-medium">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        View Challan
      </a>
    </div>
  );
}

function StepCard({ step, title, done, active, actionLabel, onAction, children }) {
  return (
    <div className={`rounded-xl border p-5 transition-colors ${done ? 'border-emerald-200 bg-emerald-50/40' : active ? 'border-brand-200 bg-brand-50/30' : 'border-gray-100 bg-gray-50/50'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-emerald-500 text-white' : active ? 'bg-brand-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
            {done ? '✓' : step}
          </span>
          <span className={`text-sm font-semibold ${done ? 'text-emerald-700' : active ? 'text-brand-700' : 'text-gray-400'}`}>{title}</span>
        </div>
        {actionLabel && onAction && (
          <button onClick={onAction} className="px-3 py-1.5 text-xs rounded-lg bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold">
            {actionLabel}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

export default function RMATicketDetail() {
  const { id } = useParams();
  const [rma, setRma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeForm, setActiveForm] = useState(null);
  const [form, setForm] = useState({});
  const [spareUnits, setSpareUnits] = useState([]);
  const [saving, setSaving] = useState(false);
  const challanRef = useRef(null);
  const returnChallanRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await client.get(`/inventory/rma-tickets/${id}/`);
      setRma(data);
    } catch { setError('Failed to load RMA ticket.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const cancel = () => { setActiveForm(null); setForm({}); setError(''); };

  const openForm = async (stage) => {
    setError('');
    setActiveForm(stage);
    setForm({});
    if (stage === 'spare_dispatched') {
      try {
        const r = await client.get('/inventory/spare-units/', { params: { status: 'available', page_size: 500 } });
        setSpareUnits(r.data.results || r.data);
      } catch { setSpareUnits([]); }
    }
  };

  const submit = async (urlPath, isMultipart = false) => {
    setSaving(true);
    setError('');
    try {
      let payload;
      if (isMultipart) {
        payload = new FormData();
        Object.entries(form).forEach(([k, v]) => { if (v !== undefined && v !== null) payload.append(k, v); });
        if (challanRef.current?.files[0]) payload.append('delivery_challan', challanRef.current.files[0]);
        if (returnChallanRef.current?.files[0]) payload.append('return_challan', returnChallanRef.current.files[0]);
      } else {
        payload = form;
      }
      const resp = await client.post(
        `/inventory/rma-tickets/${id}/${urlPath}/`,
        payload,
        isMultipart ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}
      );
      setRma(resp.data);
      setActiveForm(null);
      setForm({});
    } catch (e) {
      setError(e.response?.data?.detail || Object.values(e.response?.data || {}).flat().join(' ') || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Loading…</div>;
  if (!rma) return <div className="p-10 text-center text-red-500">{error || 'Ticket not found.'}</div>;

  const stepIndex = STATUS_ORDER.indexOf(rma.status);

  return (
    <div className="p-6 space-y-5 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-brand-500">{rma.ticket_number}</h1>
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_COLOR[rma.status] || 'bg-gray-100 text-gray-600'}`}>
              {STATUS_LABEL[rma.status] || rma.status}
            </span>
          </div>
          <p className="text-gray-400 text-sm mt-1">
            RMA child ticket for{' '}
            <Link to={`/tickets/${rma.parent_ticket}`} className="text-brand-500 hover:underline font-semibold">
              {rma.parent_ticket_number}
            </Link>
          </p>
        </div>
        <Link to="/rma-tickets" className="text-sm text-gray-400 hover:text-brand-500">← All RMA Tickets</Link>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 text-sm">{error}</div>}

      {/* Site contact chip */}
      {rma.site_contact_name && (
        <div className="flex items-start gap-2 bg-white border border-brand-100 rounded-xl px-3 py-2 w-fit">
          <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-brand-400 uppercase leading-none">BHEL Contact</p>
            <p className="text-xs font-semibold text-brand-600 mt-0.5">
              {rma.site_contact_name}
            </p>
            {(rma.site_address || rma.site_pincode) && (
              <p className="text-xs text-gray-500 mt-0.5">
                {rma.site_address}{rma.site_pincode ? ` — ${rma.site_pincode}` : ''}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Progress stepper */}
      <div className="card p-5">
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {STATUS_ORDER.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex-shrink-0 flex flex-col items-center gap-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  i < stepIndex ? 'bg-emerald-500 border-emerald-500 text-white' :
                  i === stepIndex ? 'bg-brand-500 border-brand-500 text-white' :
                  'bg-white border-gray-200 text-gray-400'
                }`}>
                  {i < stepIndex ? '✓' : i + 1}
                </div>
                <span className={`text-[10px] text-center leading-tight max-w-[60px] ${i <= stepIndex ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                  {STATUS_LABEL[s]}
                </span>
              </div>
              {i < STATUS_ORDER.length - 1 && (
                <div className={`flex-1 h-0.5 mt-[-12px] min-w-[16px] ${i < stepIndex ? 'bg-emerald-400' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">

        {/* Step 1 — RMA Details */}
        <StepCard step={1} title="RMA Details" done={stepIndex >= 0}>
          <div className="grid grid-cols-2 gap-3">
            <Field title="Part Code" value={rma.part_code} />
            <Field title="Part Description" value={rma.part_description} />
            <Field title="Faulty Serial No." value={rma.faulty_serial_number} />
            <Field title="Site Contact" value={rma.site_contact_name} />
            {rma.site_address && (
              <div className="col-span-2">
                <Field title="Site Address" value={`${rma.site_address}${rma.site_pincode ? ', ' + rma.site_pincode : ''}`} />
              </div>
            )}
            <Field title="GSTIN" value={rma.site_gstin} />
            <Field title="Notes" value={rma.notes} />
            <div>
              {lbl('Created')}
              <p className="text-sm font-medium text-gray-800">
                {new Date(rma.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>
          </div>
        </StepCard>

        {/* Step 2 — Spare Dispatch */}
        <StepCard step={2} title="Spare Dispatch" done={stepIndex >= 1} active={stepIndex === 0}
          actionLabel={stepIndex === 0 ? 'Fill Dispatch Details' : null}
          onAction={() => openForm('spare_dispatched')}>
          {stepIndex >= 1 && (
            <div className="grid grid-cols-2 gap-3">
              <Field title="Spare Serial" value={rma.spare_unit_detail?.serial_number} />
              <Field title="Dispatch Date" value={rma.dispatch_date} />
              <Field title="Courier Type" value={rma.courier_type === 'hand' ? 'By Hand' : 'Courier'} />
              {rma.courier_name && <Field title="Courier / Person" value={rma.courier_name} />}
              {rma.tracking_number && <Field title="Tracking Number" value={rma.tracking_number} />}
              <Field title="Expected Delivery" value={rma.expected_delivery_date} />
              <ChallanLink url={rma.delivery_challan_url} title="Delivery Challan" />
            </div>
          )}
          {activeForm === 'spare_dispatched' && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  {lbl('Spare Unit *')}
                  <select className={inp} value={form.spare_unit || ''} onChange={e => set('spare_unit', e.target.value)}>
                    <option value="">Select spare unit…</option>
                    {spareUnits.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.device_name} — {u.serial_number} ({u.warehouse_display || u.warehouse})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {lbl('Dispatch Date *')}
                  <input type="date" className={inp} value={form.dispatch_date || ''} onChange={e => set('dispatch_date', e.target.value)} />
                </div>
                <div>
                  {lbl('Courier Type *')}
                  <select className={inp} value={form.courier_type || ''} onChange={e => set('courier_type', e.target.value)}>
                    <option value="">Select…</option>
                    <option value="courier">Courier</option>
                    <option value="hand">By Hand</option>
                  </select>
                </div>
                {form.courier_type === 'courier' && (
                  <>
                    <div>
                      {lbl('Courier Name')}
                      <input className={inp} value={form.courier_name || ''} onChange={e => set('courier_name', e.target.value)} placeholder="BlueDart, FedEx…" />
                    </div>
                    <div>
                      {lbl('Tracking Number')}
                      <input className={inp} value={form.tracking_number || ''} onChange={e => set('tracking_number', e.target.value)} />
                    </div>
                  </>
                )}
                {form.courier_type === 'hand' && (
                  <div>
                    {lbl('Delivered By')}
                    <input className={inp} value={form.courier_name || ''} onChange={e => set('courier_name', e.target.value)} placeholder="Engineer / person name" />
                  </div>
                )}
                <div>
                  {lbl('Expected Delivery Date')}
                  <input type="date" className={inp} value={form.expected_delivery_date || ''} onChange={e => set('expected_delivery_date', e.target.value)} />
                </div>
                <div>
                  {lbl('Delivery Challan')}
                  <input type="file" ref={challanRef} accept=".pdf,.jpg,.jpeg,.png" className="text-sm text-gray-600" />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                <button onClick={() => submit('mark-dispatched', true)} disabled={saving}
                  className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
                  {saving ? 'Saving…' : 'Mark Dispatched'}
                </button>
              </div>
            </div>
          )}
        </StepCard>

        {/* Step 3 — Spare Delivered */}
        <StepCard step={3} title="Spare Delivered" done={stepIndex >= 2} active={stepIndex === 1}
          actionLabel={stepIndex === 1 ? 'Confirm Delivery' : null}
          onAction={() => openForm('spare_delivered')}>
          {stepIndex >= 2 && (
            <div className="grid grid-cols-2 gap-3">
              <Field title="Actual Delivery Date" value={rma.actual_delivery_date} />
              <Field title="Faulty → Warehouse" value={rma.faulty_spare_unit_detail?.serial_number ? `${rma.faulty_spare_unit_detail.serial_number} (Faulty)` : null} />
              <div className="col-span-2">
                <p className="text-xs text-emerald-600 font-medium">Spare serial installed in room inventory. Faulty device moved to warehouse stock.</p>
              </div>
            </div>
          )}
          {activeForm === 'spare_delivered' && (
            <div className="mt-3 space-y-3">
              <div>
                {lbl('Actual Delivery Date *')}
                <input type="date" className={inp} value={form.actual_delivery_date || ''} onChange={e => set('actual_delivery_date', e.target.value)} />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                <button onClick={() => submit('mark-delivered')} disabled={saving}
                  className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
                  {saving ? 'Saving…' : 'Confirm Delivery'}
                </button>
              </div>
            </div>
          )}
        </StepCard>

        {/* Step 4 — Faulty Pickup */}
        <StepCard step={4} title="Faulty Pickup" done={stepIndex >= 3} active={stepIndex === 2}
          actionLabel={stepIndex === 2 ? 'Enter Pickup Details' : null}
          onAction={() => openForm('faulty_picked_up')}>
          {stepIndex >= 3 && (
            <div className="grid grid-cols-2 gap-3">
              <Field title="Pickup Date" value={rma.faulty_pickup_date} />
              <Field title="Courier Type" value={rma.return_courier_type === 'hand' ? 'By Hand' : 'Courier'} />
              {rma.return_courier_name && <Field title="Courier / Person" value={rma.return_courier_name} />}
              {rma.return_tracking_number && <Field title="Tracking Number" value={rma.return_tracking_number} />}
              <ChallanLink url={rma.return_challan_url} title="Return Challan" />
            </div>
          )}
          {activeForm === 'faulty_picked_up' && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {lbl('Pickup Date *')}
                  <input type="date" className={inp} value={form.faulty_pickup_date || ''} onChange={e => set('faulty_pickup_date', e.target.value)} />
                </div>
                <div>
                  {lbl('Courier Type *')}
                  <select className={inp} value={form.return_courier_type || ''} onChange={e => set('return_courier_type', e.target.value)}>
                    <option value="">Select…</option>
                    <option value="courier">Courier</option>
                    <option value="hand">By Hand</option>
                  </select>
                </div>
                {form.return_courier_type === 'courier' && (
                  <>
                    <div>
                      {lbl('Courier Name')}
                      <input className={inp} value={form.return_courier_name || ''} onChange={e => set('return_courier_name', e.target.value)} />
                    </div>
                    <div>
                      {lbl('Tracking Number')}
                      <input className={inp} value={form.return_tracking_number || ''} onChange={e => set('return_tracking_number', e.target.value)} />
                    </div>
                  </>
                )}
                {form.return_courier_type === 'hand' && (
                  <div>
                    {lbl('Picked Up By')}
                    <input className={inp} value={form.return_courier_name || ''} onChange={e => set('return_courier_name', e.target.value)} />
                  </div>
                )}
                <div>
                  {lbl('Return Challan')}
                  <input type="file" ref={returnChallanRef} accept=".pdf,.jpg,.jpeg,.png" className="text-sm text-gray-600" />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                <button onClick={() => submit('mark-faulty-picked-up', true)} disabled={saving}
                  className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
                  {saving ? 'Saving…' : 'Mark Picked Up'}
                </button>
              </div>
            </div>
          )}
        </StepCard>

        {/* Step 5 — OEM RMA */}
        <StepCard step={5} title="OEM RMA" done={stepIndex >= 4} active={stepIndex === 3}
          actionLabel={stepIndex === 3 ? 'Raise OEM RMA' : null}
          onAction={() => openForm('oem_rma_raised')}>
          {stepIndex >= 4 && (
            <div className="grid grid-cols-2 gap-3">
              <Field title="SR Number" value={rma.oem_sr_number} />
              <Field title="RMA Number" value={rma.oem_rma_number} />
              <Field title="RMA Date" value={rma.oem_rma_date} />
            </div>
          )}
          {activeForm === 'oem_rma_raised' && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {lbl('SR Number')}
                  <input className={inp} value={form.oem_sr_number || ''} onChange={e => set('oem_sr_number', e.target.value)} />
                </div>
                <div>
                  {lbl('RMA Number *')}
                  <input className={inp} value={form.oem_rma_number || ''} onChange={e => set('oem_rma_number', e.target.value)} />
                </div>
                <div>
                  {lbl('RMA Date *')}
                  <input type="date" className={inp} value={form.oem_rma_date || ''} onChange={e => set('oem_rma_date', e.target.value)} />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                <button onClick={() => submit('raise-oem-rma')} disabled={saving}
                  className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
                  {saving ? 'Saving…' : 'Raise OEM RMA'}
                </button>
              </div>
            </div>
          )}
        </StepCard>

        {/* Step 6 — Replacement Received */}
        <StepCard step={6} title="Replacement Received" done={stepIndex >= 5} active={stepIndex === 4}
          actionLabel={stepIndex === 4 ? 'Enter Replacement Details' : null}
          onAction={() => openForm('closed')}>
          {stepIndex >= 5 && (
            <div className="grid grid-cols-2 gap-3">
              <Field title="Replacement Serial" value={rma.replacement_serial_number} />
              <Field title="Received Date" value={rma.replacement_received_date} />
              <div className="col-span-2">
                <p className="text-xs text-emerald-600 font-medium">Replacement added to spare warehouse. Faulty unit record removed.</p>
              </div>
            </div>
          )}
          {activeForm === 'closed' && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {lbl('Replacement Serial Number *')}
                  <input className={inp} value={form.replacement_serial_number || ''} onChange={e => set('replacement_serial_number', e.target.value)} />
                </div>
                <div>
                  {lbl('Received Date *')}
                  <input type="date" className={inp} value={form.replacement_received_date || ''} onChange={e => set('replacement_received_date', e.target.value)} />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={cancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                <button onClick={() => submit('mark-replacement-received')} disabled={saving}
                  className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">
                  {saving ? 'Saving…' : 'Confirm Replacement'}
                </button>
              </div>
            </div>
          )}
        </StepCard>

      </div>
    </div>
  );
}
